import React, { useState, useEffect, useMemo } from 'react';
import { getMonthsForAcademicYear, formatAcademicYear } from '../utils/monthToWeekMapping';
import AcademicYearService from '../services/AcademicYearService.ts';
import { getWeekDateRange } from '../utils/timeUtils';
import { getWeekNumber } from '../utils/helpers.ts';

const ClassSchedulerTimeFrameSelector = ({
    timePeriods,
    selectedPeriodId,
    onPeriodChange,
    selectedYear,
    availableYears,
    onYearChange,
    onMonthChange,
    selectedMonth,
    onWeekChange,
    selectedWeek,
    currentView
}) => {
    const [academicYearStructure, setAcademicYearStructure] = useState(null);
    const [isLoadingStructure, setIsLoadingStructure] = useState(true);
    const [selectedFrameType, setSelectedFrameType] = useState('whole-year');

    // Get academic year service instance
    const academicYearService = useMemo(() => AcademicYearService.getInstance(), []);

    // Fetch academic year structure
    useEffect(() => {
        const fetchAcademicStructure = async () => {
            setIsLoadingStructure(true);
            try {
                const structure = await academicYearService.getAcademicYearStructure();
                setAcademicYearStructure(structure);
            } catch (error) {
                console.warn('[ClassSchedulerTimeFrameSelector] Failed to fetch academic structure:', error);
                setAcademicYearStructure(null);
            } finally {
                setIsLoadingStructure(false);
            }
        };

        fetchAcademicStructure();
    }, [academicYearService]);

    // Get months for current academic year
    const availableMonths = useMemo(() => {
        return getMonthsForAcademicYear(academicYearStructure);
    }, [academicYearStructure]);

    // Format available years as academic years
    const formattedAvailableYears = useMemo(() => {
        return availableYears.map(year => ({
            value: year,
            label: formatAcademicYear(year),
            academicYear: formatAcademicYear(year)
        }));
    }, [availableYears]);

    // Frame type options
    const frameTypeOptions = useMemo(() => {
        const options = [
            { value: 'whole-year', label: 'Whole Year' }
        ];

        // Add periods option if available
        if (timePeriods.length > 0) {
            options.push({ value: 'periods', label: 'Periods' });
        }

        // Add months option if available
        if (availableMonths.length > 0) {
            options.push({ value: 'months', label: 'Months' });
        }

        // Add weeks option (always available)
        options.push({ value: 'weeks', label: 'Weeks' });

        return options;
    }, [timePeriods, availableMonths]);

    // Get frame selection options based on selected frame type
    const frameSelectionOptions = useMemo(() => {
        switch (selectedFrameType) {
            case 'whole-year':
                return []; // No additional selection needed
            case 'periods':
                return timePeriods.map(period => ({
                    value: period.id,
                    label: `${period.name} (Week ${period.start_week}-${period.end_week})`,
                    data: period
                }));
            case 'months':
                return availableMonths.map(month => ({
                    value: month.name,
                    label: `${month.name} (Week ${month.weekSeries}, ${month.calendarYear})`,
                    data: month
                }));
            case 'weeks':
                // Generate week options for the academic year
                if (academicYearStructure && academicYearStructure.startWeek && academicYearStructure.endWeek) {
                    const weeks = [];
                    const startWeek = academicYearStructure.startWeek;
                    const endWeek = academicYearStructure.endWeek;
                    const isCrossYear = startWeek > endWeek;

                    if (startWeek <= endWeek) {
                        // Normal academic year (e.g., weeks 1-52)
                        for (let week = startWeek; week <= endWeek; week++) {
                            const weekDateRange = getWeekDateRange(selectedYear, week);
                            const dateLabel = weekDateRange ?
                                `${weekDateRange.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekDateRange.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` :
                                '';

                            weeks.push({
                                value: week,
                                label: `Week ${week} (${dateLabel})`,
                                data: { week, year: selectedYear }
                            });
                        }
                    } else {
                        // Cross-year academic year (e.g., weeks 36-35)
                        for (let week = startWeek; week <= 53; week++) {
                            const weekDateRange = getWeekDateRange(selectedYear, week);
                            const dateLabel = weekDateRange ?
                                `${weekDateRange.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekDateRange.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` :
                                '';

                            weeks.push({
                                value: week,
                                label: `Week ${week} (${dateLabel})`,
                                data: { week, year: selectedYear }
                            });
                        }
                        for (let week = 1; week <= endWeek; week++) {
                            // Weeks after New Year belong to the next calendar year
                            const weekYear = selectedYear + 1;
                            const weekDateRange = getWeekDateRange(weekYear, week);
                            const dateLabel = weekDateRange ?
                                `${weekDateRange.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekDateRange.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` :
                                '';

                            weeks.push({
                                value: week,
                                label: `Week ${week} (${dateLabel})`,
                                data: { week, year: weekYear }
                            });
                        }
                    }
                    return weeks;
                }
                return [];
            default:
                return [];
        }
    }, [selectedFrameType, timePeriods, availableMonths, academicYearStructure, selectedYear]);

    // Handle frame type change
    const handleFrameTypeChange = (frameType) => {
        console.log('[ClassSchedulerTimeFrameSelector] handleFrameTypeChange called:', {
            from: selectedFrameType,
            to: frameType,
            currentSelections: {
                selectedPeriodId,
                selectedMonth: selectedMonth?.month,
                selectedWeek: selectedWeek?.week
            }
        });

        setSelectedFrameType(frameType);

        // Clear previous selections
        onPeriodChange(null);
        if (onMonthChange) {
            onMonthChange(null);
        }
        if (onWeekChange) {
            onWeekChange(null);
        }
    };

    // Handle frame selection change
    const handleFrameSelectionChange = (value) => {
        console.log('[ClassSchedulerTimeFrameSelector] handleFrameSelectionChange called:', {
            value,
            selectedFrameType,
            currentSelections: {
                selectedPeriodId,
                selectedMonth: selectedMonth?.month,
                selectedWeek: selectedWeek?.week
            }
        });

        if (value === '') return;

        switch (selectedFrameType) {
            case 'whole-year':
                // No additional action needed
                break;
            case 'periods':
                const periodId = parseInt(value);
                onPeriodChange(periodId);
                if (onWeekChange) {
                    onWeekChange(null);
                }
                break;
            case 'months':
                const monthName = value;
                const monthData = availableMonths.find(m => m.name === monthName);
                onPeriodChange(null); // Clear period selection
                if (onWeekChange) {
                    onWeekChange(null);
                }
                if (onMonthChange && monthData) {
                    onMonthChange({
                        month: monthName,
                        weekSeries: monthData.weekSeries,
                        weekRange: monthData.weekRange,
                        calendarYear: monthData.calendarYear
                    });
                }
                break;
            case 'weeks':
                // Week selected
                const weekNumber = parseInt(value);
                if (onWeekChange) {
                    const isCrossYear = academicYearStructure && academicYearStructure.startWeek > academicYearStructure.endWeek;
                    const computedYear = isCrossYear && academicYearStructure
                        ? (weekNumber <= academicYearStructure.endWeek ? selectedYear + 1 : selectedYear)
                        : selectedYear;
                    onWeekChange({
                        week: weekNumber,
                        year: computedYear
                    });
                }
                // Clear other selections
                onPeriodChange(null);
                if (onMonthChange) {
                    onMonthChange(null);
                }
                break;
        }
    };

    // Get current frame selection value
    const getCurrentFrameSelectionValue = () => {
        if (selectedPeriodId) {
            return selectedPeriodId.toString();
        }
        if (selectedMonth) {
            return selectedMonth.month;
        }
        if (selectedWeek) {
            return selectedWeek.week.toString();
        }
        return '';
    };

    // Determine current frame type based on selections with clear precedence
    // Precedence: periods > months > weeks. Avoid fall-through toggling when multiple are set.
    useEffect(() => {
        console.log('[ClassSchedulerTimeFrameSelector] Frame type determination effect triggered:', {
            selectedPeriodId,
            selectedMonth: selectedMonth?.month,
            selectedWeek: selectedWeek?.week,
            selectedFrameType
        });

        // If a period is selected, it takes precedence
        if (selectedPeriodId) {
            if (selectedFrameType !== 'periods') {
                console.log('[ClassSchedulerTimeFrameSelector] Auto-setting frame type to periods');
                setSelectedFrameType('periods');
            }
            return;
        }

        // If a month is selected, it takes next precedence
        if (selectedMonth) {
            if (selectedFrameType !== 'months') {
                console.log('[ClassSchedulerTimeFrameSelector] Auto-setting frame type to months');
                setSelectedFrameType('months');
            }
            return;
        }

        // If a week is selected, set weeks
        if (selectedWeek) {
            if (selectedFrameType !== 'weeks') {
                console.log('[ClassSchedulerTimeFrameSelector] Auto-setting frame type to weeks');
                setSelectedFrameType('weeks');
            }
            return;
        }

        console.log('[ClassSchedulerTimeFrameSelector] No frame type change needed - respecting user choice');
    }, [selectedPeriodId, selectedMonth, selectedWeek]);

    // When switching to "Weeks", default to the current week instead of "Select Week"
    useEffect(() => {
        if (selectedFrameType !== 'weeks') return;
        if (typeof onWeekChange !== 'function') return;
        if (selectedWeek) return; // already selected
        if (isLoadingStructure) return;

        const now = new Date();
        const currentWeek = getWeekNumber(now);
        const currentWeekYear = now.getFullYear(); // Calendar year for the ISO week (good enough for our needs)

        // If we have structure bounds, ensure the "current week" belongs to the selected academic year.
        const startWeek = academicYearStructure?.startWeek;
        const endWeek = academicYearStructure?.endWeek;
        const hasBounds = Number.isFinite(startWeek) && Number.isFinite(endWeek);

        let week = currentWeek;
        let year = currentWeekYear;

        if (hasBounds) {
            const isCrossYear = startWeek > endWeek;
            const inSelectedAcademicYear = !isCrossYear
                ? (year === selectedYear && week >= startWeek && week <= endWeek)
                : ((year === selectedYear && week >= startWeek) || (year === selectedYear + 1 && week <= endWeek));

            if (!inSelectedAcademicYear) {
                // If the current week isn't in the selected academic year, keep "Select Week"
                return;
            }

            // Normalize year for cross-year academic years based on the week number
            if (isCrossYear) {
                year = week <= endWeek ? selectedYear + 1 : selectedYear;
            } else {
                year = selectedYear;
            }
        }

        onWeekChange({ week, year });
        // Clear other selections for consistency
        onPeriodChange(null);
        if (onMonthChange) onMonthChange(null);
    }, [selectedFrameType, selectedWeek, onWeekChange, onPeriodChange, onMonthChange, isLoadingStructure, academicYearStructure, selectedYear]);

    // If in room view, force whole year selection
    useEffect(() => {
        if (currentView === 'room') {
            console.log('[ClassSchedulerTimeFrameSelector] Room view detected, forcing whole year selection');
            setSelectedFrameType('whole-year');
            onPeriodChange(null);
            if (onMonthChange) {
                onMonthChange(null);
            }
            if (onWeekChange) {
                onWeekChange(null);
            }
        }
    }, [currentView, onPeriodChange, onMonthChange, onWeekChange]);

    return (
        <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
            {/* Academic Year Selection */}
            <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Academic Year:</label>
                <select
                    value={selectedYear}
                    onChange={(e) => onYearChange(parseInt(e.target.value))}
                    className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isLoadingStructure}
                >
                    {formattedAvailableYears.map(yearData => (
                        <option key={yearData.value} value={yearData.value}>
                            {yearData.label}
                        </option>
                    ))}
                </select>
                {isLoadingStructure && (
                    <span className="text-xs text-gray-500">Loading...</span>
                )}
            </div>

            <div className="h-4 w-px bg-gray-300"></div>

            {/* Frame Type Selection */}
            <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Range:</label>
                <select
                    value={selectedFrameType}
                    onChange={(e) => handleFrameTypeChange(e.target.value)}
                    className={`px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-32 ${currentView === 'room' ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                    disabled={currentView === 'room'}
                >
                    {frameTypeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* Frame Selection (only show if not whole year) */}
            {selectedFrameType !== 'whole-year' && frameSelectionOptions.length > 0 && (
                <>
                    <div className="h-4 w-px bg-gray-300"></div>
                    <div className="flex items-center gap-2">
                        {/* Left Arrow */}
                        <button
                            onClick={() => {
                                const currentValue = getCurrentFrameSelectionValue();
                                const currentIndex = frameSelectionOptions.findIndex(option => option.value.toString() === currentValue);
                                if (currentIndex > 0) {
                                    handleFrameSelectionChange(frameSelectionOptions[currentIndex - 1].value.toString());
                                } else if (currentIndex === 0) {
                                    // Wrap to last option
                                    handleFrameSelectionChange(frameSelectionOptions[frameSelectionOptions.length - 1].value.toString());
                                } else if (currentIndex === -1 && frameSelectionOptions.length > 0) {
                                    // If nothing is selected, go to last option
                                    handleFrameSelectionChange(frameSelectionOptions[frameSelectionOptions.length - 1].value.toString());
                                }
                            }}
                            className={`p-1 rounded hover:bg-gray-200 transition-colors ${currentView === 'room' ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:text-gray-800'}`}
                            disabled={currentView === 'room'}
                            title="Previous option"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        <select
                            value={getCurrentFrameSelectionValue()}
                            onChange={(e) => handleFrameSelectionChange(e.target.value)}
                            className={`px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-48 ${currentView === 'room' ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                            disabled={currentView === 'room'}
                        >
                            <option value="">Select {selectedFrameType.slice(0, -1)}</option>
                            {frameSelectionOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>

                        {/* Right Arrow */}
                        <button
                            onClick={() => {
                                const currentValue = getCurrentFrameSelectionValue();
                                const currentIndex = frameSelectionOptions.findIndex(option => option.value.toString() === currentValue);
                                if (currentIndex >= 0 && currentIndex < frameSelectionOptions.length - 1) {
                                    handleFrameSelectionChange(frameSelectionOptions[currentIndex + 1].value.toString());
                                } else if (currentIndex === frameSelectionOptions.length - 1) {
                                    // Wrap to first option
                                    handleFrameSelectionChange(frameSelectionOptions[0].value.toString());
                                } else if (currentIndex === -1 && frameSelectionOptions.length > 0) {
                                    // If nothing is selected, go to first option
                                    handleFrameSelectionChange(frameSelectionOptions[0].value.toString());
                                }
                            }}
                            className={`p-1 rounded hover:bg-gray-200 transition-colors ${currentView === 'room' ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:text-gray-800'}`}
                            disabled={currentView === 'room'}
                            title="Next option"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default ClassSchedulerTimeFrameSelector;
